// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
import * as React from 'react';
import { featureIsEnabled } from '@ta/common.features';
import { Link } from '@ta/common.routing';
import ObfuscatedLink from '@ta/common.obfuscated-link';
import { localize } from '@ta/common.i18n';
import { InteractionLogger } from '@ta/tracking.interactions';

import styles from './Linkify.less';

type SocialReference = {
  __typename: 'UserReference',
  length: number,
  offset: number,
  userName?: string,
  userId?: string,
};

type Categorization = 'OK' | 'QUESTIONABLE' | 'NOT_OK';

type LinkReference = {
  __typename: 'LinkReference',
  length: number,
  offset: number,
  url: string,
  encodedQualifiedUrl: string,
  categorization: Categorization,
};
type Reference = LinkReference | SocialReference;

/** Object describing social references in text */
export type SocialReferences = {
  /** The social references for user at-references */
  userReferences?: SocialReference[],
  /** The link references if any used for external links in the content */
  linkReferences?: LinkReference[],
};

type Props = {|
  /** The text to linkify. This should be the original, unmodified text, with the exception of truncation at the end */
  text: string,
  /** The social references to be linkified by this component (i.e. @user-references, hashtags) */
  socialReferences: SocialReferences,
  /** Whether span with onClick should be used for links, or Link component with anchor tag (default) */
  useSpan?: boolean,
  /** Whether the text should be trimmed by removing whitespace and the start and end */
  trim?: boolean,
  /** Optional event to fire on any reference click */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  /** Optional event to fire on @user reference click */
  onLinkReferenceClick?: (SyntheticMouseEvent<HTMLElement>, string) => void,
  /** Optional event to fire on link reference click */
  onUserReferenceClick?: (SyntheticMouseEvent<HTMLElement>) => void,
|};

const trimStart = (str: string) => str.replace(/^\s+/g, '');
const trimEnd = (str: string) => str.replace(/\s+$/g, '');

const getLinkText = (
  ref: Reference,
  text: string,
  referenceOffset: number,
  referenceEnd: number,
) => {
  if (ref.__typename === 'LinkReference' && ref.categorization === 'NOT_OK') {
    // <link_removed>
    return localize('linkify_removed_v2');
  }

  return text.substring(referenceOffset, referenceEnd);
};

const getHref = (ref: Reference) => {
  let href = '#';
  if (ref.__typename === 'UserReference' && ref.userName) {
    href = `/Profile/${encodeURIComponent(ref.userName)}`;
  } else if (ref.__typename === 'LinkReference') {
    if (ref.categorization !== 'NOT_OK' && ref.encodedQualifiedUrl) {
      href = ref.encodedQualifiedUrl;
    }
  }

  return href;
};

const renderProfileLinkWithSpan = (
  linkText: string,
  href: string,
  onClick: (SyntheticMouseEvent<HTMLElement>) => mixed,
) => (
  <span
    className={styles.link_user_reference}
    dir="ltr"
    onClick={e => {
      onClick(e);
      e.preventDefault();
      e.stopPropagation();
      // eslint-disable-next-line no-restricted-globals
      window.location.href = href;
    }}
  >
    {linkText}
  </span>
);

const renderProfileLinkWithLink = (
  linkText: string,
  href: string,
  onClick: (SyntheticMouseEvent<HTMLElement>) => mixed,
) => (
  <Link href={href} className={styles.link_user_reference} dir="ltr" onClick={onClick}>
    {linkText}
  </Link>
);

const renderProfileLink = (
  logUserRef,
  linkText: string,
  href: string,
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  onUserReferenceClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  useSpan?: boolean,
  reference: SocialReference,
) => {
  const onClickPlusTracking = e => {
    logUserRef(e, 'userReferenceClick', {
      targetUserId: reference.userId,
    });
    if (onClick) {
      onClick(e);
    }
    if (onUserReferenceClick) {
      onUserReferenceClick(e);
    }
  };

  return useSpan
    ? renderProfileLinkWithSpan(linkText, href, onClickPlusTracking)
    : renderProfileLinkWithLink(linkText, href, onClickPlusTracking);
};

const renderExternalLink = (
  linkText: string,
  href: string,
  urlInPlainText: string,
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  onLinkReferenceClick?: (SyntheticMouseEvent<HTMLElement>, string) => void,
  useSpan?: boolean,
) => {
  // href can be '#' or the encodedUrl
  if (href === '#') {
    return useSpan ? <span>{linkText}</span> : <div>{linkText}</div>;
  }
  return (
    <ObfuscatedLink
      target="_blank"
      onClick={e => {
        if (onClick) {
          onClick(e);
        }
        if (onLinkReferenceClick) {
          onLinkReferenceClick(e, urlInPlainText);
        }
        e.preventDefault();
        e.stopPropagation();
      }}
      className={styles.external_link}
      useSpan={useSpan}
      encodedUrl={href}
    >
      {linkText}
    </ObfuscatedLink>
  );
};

const renderLinkify = (
  linkKey: string,
  linkText: string,
  href: string,
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  onLinkReferenceClick?: (SyntheticMouseEvent<HTMLElement>, string) => void,
  onUserReferenceClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  useSpan?: boolean,
  reference: Reference,
) => (
  <InteractionLogger key={linkKey}>
    {logUserRef =>
      reference.__typename === 'UserReference'
        ? renderProfileLink(
            logUserRef,
            linkText,
            href,
            onClick,
            onUserReferenceClick,
            useSpan,
            reference,
          )
        : renderExternalLink(linkText, href, reference.url, onClick, onLinkReferenceClick, useSpan)
    }
  </InteractionLogger>
);

/**
 * Linkifies the given text by creating a Link component around any content references,
 * or a span with an onClick navigating to the corresponding url if useSpan is set to true.
 */
const Linkify = ({
  text,
  socialReferences,
  useSpan,
  trim,
  onClick,
  onLinkReferenceClick,
  onUserReferenceClick,
}: Props) => {
  if (text && socialReferences) {
    // Merge all the content references types and add refType
    let allReferences: Reference[] = [];
    if (
      featureIsEnabled('social_at_referencing_profile_links') &&
      socialReferences.userReferences
    ) {
      allReferences = allReferences.concat(socialReferences.userReferences);
    }

    if (featureIsEnabled('linkify_urls') && socialReferences.linkReferences) {
      allReferences = allReferences.concat(socialReferences.linkReferences);
    }

    if (allReferences.length) {
      // Sort by offset ascending so we can process input text from start to end
      allReferences.sort((a: Reference, b: Reference) => a.offset - b.offset);

      let currentOffset = 0;
      const linkifiedTextItems = [];
      for (let i = 0; i < allReferences.length; i++) {
        const contentReference = allReferences[i];
        const referenceOffset = contentReference.offset;
        const referenceEnd = referenceOffset + contentReference.length;

        // Add the text up to the next reference
        let nonRefText = text.substring(currentOffset, referenceOffset);
        if (trim && i === 0) {
          nonRefText = trimStart(nonRefText);
        }
        linkifiedTextItems.push(nonRefText);
        currentOffset = referenceOffset;

        if (referenceEnd > text.length) {
          // The current contentReference exceeds the length of the input text.
          // This happens if the caller truncated the text before passing in,
          // but CTA service analyzed the non-truncated version.
          break;
        }

        // Linkify!
        const linkText = getLinkText(contentReference, text, referenceOffset, referenceEnd);
        const linkKey = `linkified_text_item_${i}`;
        const href = getHref(contentReference);

        linkifiedTextItems.push(
          renderLinkify(
            linkKey,
            linkText,
            href,
            onClick,
            onLinkReferenceClick,
            onUserReferenceClick,
            useSpan,
            contentReference,
          ),
        );

        currentOffset = referenceEnd;
      }

      // Add the remainder of the text
      if (currentOffset < text.length) {
        let remainingText = text.substring(currentOffset);
        if (trim) {
          remainingText = trimEnd(remainingText);
        }
        linkifiedTextItems.push(remainingText);
      }

      return <React.Fragment>{linkifiedTextItems}</React.Fragment>;
    }
  }

  return <React.Fragment>{trim ? text.trim() : text}</React.Fragment>;
};

Linkify.defaultProps = {
  text: '',
  socialReferences: {},
  useSpan: false,
  trim: false,
  onClick: undefined,
  onLinkReferenceClick: undefined,
  onUserReferenceClick: undefined,
};

export default Linkify;
